import {AfterViewInit, Component, OnInit, Renderer2, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {UtilsService} from "../shared/utils.service";
import {AppService} from "../app.service";
import {ApiRequestService} from "../shared/api-request.service";
import {MatDialog} from "@angular/material/dialog";
import {tap} from "rxjs/operators";
import {UserPublicProfileComponent} from "../shared/user-public-profile/user-public-profile.component";
import {ApiDataSource} from "../utils/api-data-source";
import {AssetTypeModel} from "../models/asset-type.model";
import {AssetTypesViewComponent} from "./asset-types-view/asset-types-view.component";
import {AssetTypesEditComponent} from "./asset-types-edit/asset-types-edit.component";
import {AssetTypesFilterComponent} from "./asset-types-filter/asset-types-filter.component";
import {saveAs} from "file-saver";
import moment from "moment";
import {RBACService} from "../shared/rbac.service";
import {Router} from "@angular/router";

@Component({
  selector: 'app-asset-types',
  templateUrl: './asset-types.component.html',
  styleUrls: ['./asset-types.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetTypesComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new AssetTypesDataSource(this.app, this.api);

  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // Build displayedColumns dynamically (NOT as a static array).
  displayedColumns: string[] = [];

  on_parent_list: boolean = true;

  constructor(
    private utils: UtilsService,
    private app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    public renderer: Renderer2,
    public rbac: RBACService,
    private router: Router
  ) { }

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSET_TYPES, RBACService.RBAC_A.A_CREATE)
    ], true);
    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSET_TYPES, RBACService.RBAC_A.A_VIEW)
    ], true);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSET_TYPES, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSET_TYPES, RBACService.RBAC_A.A_DELETE)
    ], true);

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to access asset types.');
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [];
    if (this.canView || this.canDelete) {
      this.displayedColumns.push('select');
    }
    this.displayedColumns.push(
      'id',
      'name',
      'description',
      'linked_assets_count',
      'created_by_user',
      'date_created',
      'buttons'
    );

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.dataSource.getData(false);
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === "date_created_UTC") {
        this.dataSource.order_by = "date_created";
      } else {
        this.dataSource.order_by = this.sort.active;
      }


      this.dataSource.getData(false);
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Create a new Asset Type.
   *
   * @return {void}
   */
  onAdd(): void {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create asset types.');
      return;
    }
    // Deliberately not routed through onEdit(), which would check the edit permission and block a
    // create-only user.
    this.openDialog(new AssetTypeModel());
  }

  /**
   * Update an existing Asset Type.
   *
   * @param {AssetTypeModel} assetType The asset type to be edited.
   * @return {void}
   */
  onEdit(assetType: AssetTypeModel): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit asset types.');
      return;
    }
    this.openDialog(assetType);
  }

  /**
   * Opens the create/edit dialog. Shared by onAdd() and onEdit(), which each guard their own
   * permission before calling this.
   *
   * @param {AssetTypeModel} assetType The asset type to be created or edited.
   * @return {void}
   */
  private openDialog(assetType: AssetTypeModel): void {
    this.dialog
      .open(AssetTypesEditComponent, {
        width: '900px',
        data: {
          asset_type_id: assetType.id,
          on_parent_list: this.on_parent_list
        }
      })
      .afterClosed()
      .subscribe(() => {
        // Refresh the list regardless of how the dialog is closed, but only for a user who may
        // actually see the list.
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  /**
   * Display Asset Type data.
   *
   * @param {number} id - The unique identifier of the asset type.
   * @return {void}
   */
  onView(id: number): void {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view asset types.');
      return;
    }
    this.utils.showComponentDialog(
      AssetTypesViewComponent,
      id,
      { width: '700px' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Archives Asset Types and any associated Subtypes.
   *
   * @return {void}
   */
  onRemoveSelected(): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive asset types.');
      return;
    }
    this.utils.showModal(
      'Archive Asset Type(s)',
      `Are you sure you want to archive ${this.dataSource.selection.selected.length} asset type(s)? If there are any Subtypes then they will be archived as well.`,
      () => {
        this.remove(this.dataSource.selection.selected.join(','), 'Asset Type(s) have been archived.');
      }
    );
  }

  /**
   * Archives the specified Asset Type and any associated Subtypes.
   *
   * @param {AssetTypeModel} assetType - The asset type to be archived.
   * @return {void}
   */
  onRemove(assetType: AssetTypeModel): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive asset types.');
      return;
    }
    this.utils.showModal(
      'Archive Asset Type',
      `Are you sure you want to archive "${assetType.name}"? If there are any Subtypes then they will be archived as well.`,
      () => {
        this.remove(assetType.id, 'Asset Type has been archived.');
      }
    );
  }

  /**
   * Archives Asset Types with the specified IDs.
   *
   * @param {any} ids - The asset type IDs to be removed.
   * @param msg
   * @return {void}
   */
  private remove(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v2/asset-types/${ids}`)
      .then((response) => {
        // The backend returns a specific message when some types could not be archived
        // because they are linked to assets; prefer it over the generic message.
        this.utils.showToast(response?.message || msg);
        this.dataSource.selection.clear();
        this.dataSource.getData(false);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Restore Asset Type.
   */
  onRestore(id: number) {
    // Restoring is the inverse of archiving, so it is governed by the delete permission.
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore asset types.');
      return;
    }
    this.utils.showModal(
      'Restore Asset Type',
      'Are you sure you want to restore this asset type?',
      () => {
        this.restore(id, 'The Asset Type was restored.');
      }
    );
  }

  /**
   * Restore selected Asset Types.
   */
  onRestoreSelected() {
    // Restoring is the inverse of archiving, so it is governed by the delete permission.
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore asset types.');
      return;
    }
    this.utils.showModal(
      'Restore Selected Asset Types',
      'Are you sure you want to restore the selected asset types?',
      () => {
        this.restore(this.dataSource.selection.selected.join(','), 'The Asset Type(s) were restored.');
      }
    );
  }

  /**
   * Restore Asset Types with the specified IDs.
   *
   * @param {any} ids - The asset type IDs to be restored.
   * @param msg
   * @return {void}
   */
  private restore(ids: any, msg: string): void {
    this.api.makeRequest('put', `v2/asset-types/${ids}/restore`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Show component dialog for filtering Asset Types.
   *
   * @return {void}
   */
  onFilter(): void {
    // Filtering only narrows the list, so it is meaningless without the view permission.
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view asset types.');
      return;
    }
    this.utils.showComponentDialog(AssetTypesFilterComponent, {
        filter_archived: this.dataSource.archived,
        filter_on_parent_list: this.on_parent_list
      }, {
        width: '500px'
      },
      (results: any): void => {
        // Terminate early if it is undefined or an empty string.
        if ( typeof results === 'undefined' || results === '' ) {
          return;
        }

        // Apply the filters.
        this.dataSource.archived = results.archived ?? this.dataSource.archived;
        this.dataSource.getData();
      }
    );
  }

  /**
   * Exports the list of Asset Types into the specified format and sends a download request to the browser.
   *
   * @param type csv, xls, xlsx, pdf.
   */
  onExportSelected(type: string = 'csv'): void {
    // Exporting reveals the records themselves, so it requires the view permission.
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to export asset types.');
      return;
    }
    this.dataSource.makeDownloadRequest(`v2/asset-types/export/${type}` + (this.dataSource.selection.selected.length ? '/' + this.dataSource.selection.selected.join(',') : ''), {
      archived: this.dataSource.archived
    })
      .then((response) => {
        saveAs(response, `Asset Types - ${moment().toString()}.${type}`);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Open the User's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;
}

export class AssetTypesDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';
  archived = "false";

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/asset-types', resetOffset, {
      archived: this.archived
    });
  }
}
